<template>
    <!-- UI-03: la heatmap e' una card FULL WIDTH. L'altezza segue il contenuto:
         nessun h-100 ereditato dalla vecchia griglia 7/5 (che lasciava il grande
         spazio bianco sotto i 12 mesi). -->
    <div class="card mb-2">
        <div class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h3 class="card-title mb-0">
                Andamento annuale
                <span v-if="disponibile" class="text-muted font-size-sm ms-1">{{ heatmap.anno }}</span>
            </h3>
            <div class="btn-group btn-group-sm" role="group" aria-label="Metrica della heatmap">
                <button v-for="m in metricheDisponibili" :key="m.key" type="button" class="btn"
                        :class="metrica === m.key ? 'btn-primary' : 'btn-outline-secondary'"
                        :aria-pressed="metrica === m.key ? 'true' : 'false'"
                        :disabled="!disponibile"
                        @click="metrica = m.key">{{ m.label }}</button>
            </div>
        </div>
        <div class="card-body">
            <div v-if="loading" class="placeholder-glow text-center py-5">
                <span class="placeholder col-12 placeholder-lg"></span>
            </div>

            <BlockState v-else-if="nonDisponibile" stato="non_disponibile"
                        dettaglio="La heatmap annuale non è disponibile per questa richiesta." />

            <template v-else>
                <p class="text-muted small mb-2">
                    Confronto sullo stesso mese dell'anno precedente
                    <template v-if="regolaAnno">({{ regolaAnno }})</template>.
                </p>
                <div class="row g-2" role="list">
                <!-- UI-03A: 6 colonne x 2 righe su desktop (>=1200px, xl). NON 12 su
                     una riga. Sotto: tablet 3x4 (md), mobile 2x6 (col-6). La
                     griglia resta quella Bootstrap del progetto: nessun CSS Grid
                     parallelo, nessun repeat(12,...). -->
                    <div v-for="m in mesi" :key="m.month" class="col-6 col-md-4 col-xl-2" role="listitem">
                        <!-- UI-07: la cella e' interattiva (drill del mese).
                             .stop sul link "N progetti": senza, il click aprirebbe
                             anche la modale insieme alla Quick View. -->
                        <div class="border rounded p-2 h-100 ui07-cella"
                             :style="stileCasella(m)"
                             tabindex="0" role="button"
                             :aria-label="descrizioneMese(m)"
                             @click="apriDrill(m)"
                             @keydown.enter.prevent="apriDrill(m)"
                             @keydown.space.prevent="apriDrill(m)">
                            <div class="d-flex justify-content-between align-items-start">
                                <span class="text-muted small text-uppercase">{{ m.label }}</span>
                                <!-- CASO A/B/C: niente badge numerico ambiguo. -->
                                <span v-if="elencoMese(m).length === 1" class="text-muted small text-truncate ui07-nome1"
                                      :title="titoloProgetto(elencoMese(m)[0])">
                                    {{ nomeProgetto(elencoMese(m)[0]) }}
                                </span>
                                <button v-else-if="elencoMese(m).length > 1" type="button"
                                        class="btn btn-link btn-sm p-0 ui07-link-progetti text-decoration-none"
                                        @click.stop="toggleQuickView(m)"
                                        :title="'Anteprima dei progetti di ' + m.label">
                                    {{ elencoMese(m).length }} progetti ›
                                </button>
                                <span v-else-if="elencoMese(m).length === 0" class="text-muted small fst-italic">
                                    Nessun progetto
                                </span>
                            </div>
                            <div class="fw-semibold small">{{ valoreTesto(m) }}</div>
                            <div class="text-muted" style="font-size: .72rem" v-if="deltaTesto(m)">
                                {{ deltaTesto(m) }}
                            </div>
                            <div class="text-muted" style="font-size: .72rem" v-else>—</div>

                            <!-- QUICK VIEW: pannello contestuale leggero, non una modale.
                                 Scroll interno se i progetti sono molti. -->
                            <div v-if="quickAperto === m.month" class="ui07-quickview"
                                 @click.stop role="dialog" :aria-label="'Progetti di ' + m.label">
                                <div class="ui07-quickview-titolo">PROGETTI DI {{ m.label.toUpperCase() }}</div>
                                <div class="ui07-quickview-lista">
                                    <div v-for="p in elencoMese(m)" :key="p.project_id" class="ui07-quickview-riga">
                                        <div class="fw-semibold text-truncate" :title="titoloProgetto(p)">
                                            {{ nomeProgetto(p) }}
                                        </div>
                                        <div class="text-muted font-size-sm text-truncate">{{ p.code }}</div>
                                    </div>
                                </div>
                                <button type="button" class="btn btn-link btn-sm p-0 ui07-quickview-piu"
                                        @click.stop="apriDrill(m)">
                                    Vedi dettaglio completo →
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <p class="text-muted small mt-3 mb-0">
                    Il colore indica l'intensità del valore rispetto al massimo dell'anno
                    per la metrica selezionata. Un costo elevato non è un risultato positivo:
                    il colore è solo intensità.
                </p>
            </template>
        </div>
    </div>
</template>


<script>
import BlockState from "./BlockState.vue";

const FORMAT_EUR = new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0,
});

const METRICHE = [
    { key: "fatturato", label: "Fatturato" },
    { key: "margine", label: "Margine" },
    { key: "costi", label: "Costi" },
    { key: "budget", label: "Budget" },
];

export default {
    name: "HeatmapGrid",
    components: { BlockState },

    props: {
        heatmap: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        /* UI-07: stessi filtri della Dashboard, perche' il drill del mese deve
           rappresentare lo STESSO perimetro della heatmap. */
        filtri: { type: Object, default: null },
        periodo: { type: String, default: "" },
    },

    data() {
        /* UI-07: mese di cui e' aperta la Quick View (null = nessuna). */
        return { metrica: "fatturato", quickAperto: null };
    },

    computed: {
        disponibile() {
            return !this.loading && this.heatmap !== null && this.heatmap !== undefined
                && Array.isArray(this.heatmap.mesi);
        },
        nonDisponibile() {
            return !this.loading && !this.disponibile;
        },
        mesi() {
            return this.disponibile ? this.heatmap.mesi : [];
        },
        regolaAnno() {
            return this.disponibile ? this.heatmap.regola_anno : "";
        },
        /* L'ordine e' quello dichiarato dal backend; nessuna metrica inventata. */
        metricheDisponibili() {
            const dichiarate = this.disponibile && Array.isArray(this.heatmap.metriche)
                ? this.heatmap.metriche : METRICHE.map((m) => m.key);
            return METRICHE.filter((m) => dichiarate.includes(m.key));
        },
        massimo() {
            let max = 0;
            this.mesi.forEach((m) => {
                const d = m[this.metrica];
                const v = d && typeof d.valore === "number" ? Math.abs(d.valore) : 0;
                if (v > max) max = v;
            });
            return max;
        },
    },

    methods: {
        valoreTesto(m) {
            const d = m[this.metrica];
            return d ? FORMAT_EUR.format(d.valore) : "—";
        },
        deltaTesto(m) {
            const d = m[this.metrica];
            if (!d || d.delta === null || d.delta === undefined) return "";
            const segno = d.delta > 0 ? "+" : "";
            let testo = segno + FORMAT_EUR.format(d.delta);
            if (d.delta_percent !== null && d.delta_percent !== undefined) {
                const s = d.delta_percent > 0 ? "+" : "";
                testo += " (" + s + Math.round(d.delta_percent) + "%)";
            }
            return testo;
        },
        /* Intensita' monocromatica: piu' scuro = valore piu' alto. Nessun rosso/verde
           semantico, per non far sembrare un costo alto un buon risultato. */
        stileCasella(m) {
            const d = m[this.metrica];
            const v = d && typeof d.valore === "number" ? Math.abs(d.valore) : 0;
            const ratio = this.massimo > 0 ? v / this.massimo : 0;
            const alpha = 0.04 + ratio * 0.30;
            return "background-color: rgba(59,130,246," + alpha.toFixed(3) + ")";
        },
        /* UI-07: progetti del mese in forma compatta (dal backend). Nessun
           fallback su progettiElenco: quell'elenco ha un LIMIT e potrebbe essere
           troncato, quindi derivare il mese da li' mostrerebbe dati sbagliati. */
        elencoMese(m) {
            return (m && Array.isArray(m.progetti_elenco)) ? m.progetti_elenco : [];
        },
        nomeProgetto(p) {
            if (!p) return "";
            return (p.title && p.title !== "") ? p.title : "(senza titolo)";
        },
        titoloProgetto(p) {
            if (!p) return "";
            return (p.code ? p.code + " — " : "") + this.nomeProgetto(p);
        },
        /* Click sulla cella = drill del mese. Chiude la Quick View aperta.
           UI-05B: passa anche la METRICA selezionata, cosi' la modale si apre
           sulla metrica che l'utente sta analizzando. */
        apriDrill(m) {
            this.quickAperto = null;
            this.$emit("drill", m.month, this.metrica);
        },
        /* Click su "N progetti": apre la Quick View e NON la modale (@click.stop). */
        toggleQuickView(m) {
            this.quickAperto = (this.quickAperto === m.month) ? null : m.month;
        },
        descrizioneMese(m) {
            const d = m[this.metrica];
            if (!d) return m.label + ": dato non disponibile";
            const n = this.elencoMese(m).length;
            const chi = n === 0 ? "nessun progetto"
                : (n === 1 ? "1 progetto: " + this.titoloProgetto(this.elencoMese(m)[0])
                    : n + " progetti");
            let t = m.label + ": " + FORMAT_EUR.format(d.valore) + ", " + chi
                + ". Anno precedente: " + FORMAT_EUR.format(d.precedente);
            if (d.delta_percent !== null && d.delta_percent !== undefined) {
                t += ", variazione " + Math.round(d.delta_percent) + " percento.";
            }
            return t;
        },
    },
}
</script>

<style scoped>
/* UI-07: interazione della cella e Quick View.
   Nessuna altezza in px: la cella cresce solo di quanto serve. */
.ui07-cella {
    cursor: pointer;
    transition: box-shadow .12s ease-in-out;
}
.ui07-cella:hover,
.ui07-cella:focus-visible {
    box-shadow: 0 0 0 2px rgba(31, 78, 156, .35);
    outline: none;
}
.ui07-nome1 {
    max-width: 62%;
    display: inline-block;
}
.ui07-link-progetti {
    font-size: .72rem;
    color: #1f4e9c;
    white-space: nowrap;
}
/* Pannello contestuale: leggero, con scroll interno se i progetti sono molti. */
.ui07-quickview {
    margin-top: 6px;
    padding: 8px;
    border: 1px solid #e9edf2;
    border-radius: 8px;
    background-color: #fff;
    box-shadow: 0 6px 18px rgba(15, 23, 42, .12);
    position: relative;
    z-index: 5;
}
.ui07-quickview-titolo {
    font-size: .68rem;
    letter-spacing: .03em;
    color: #94a3b8;
    margin-bottom: 4px;
}
.ui07-quickview-lista {
    max-height: 160px;
    overflow-y: auto;
}
.ui07-quickview-riga {
    padding: 3px 0;
    border-bottom: 1px solid #f1f5f9;
}
.ui07-quickview-riga:last-child {
    border-bottom: 0;
}
.ui07-quickview-piu {
    font-size: .72rem;
    color: #1f4e9c;
    margin-top: 4px;
}
</style>
